import React, { memo, useEffect, useCallback, useMemo } from "react";
import "./HawkeyeV2CreateFilterPanel.scss";
import { useAxiomState } from "__AxiomComponents/AxiomHooks/AxiomHooks";
import Icon from "../Icons/Icon";
import FilterPanelOptions from "./FilterPanelOptions";
import { getSelectedFilters } from "../HawkeyeV2Filters/HawkeyeV2FilterUtils";

function HawkeyeV2CreateFilterPanel(props) {
  const {
    widget,
    savedFilters,
    filterOptions,
    smartFilterRefs,
    saveFilter,
    updateSavedFilter,
    closeCreateFilter,
    HawkeyeV2AppSettings = {},
  } = props;

  const [state, dispatch] = useAxiomState(
    {
      selectedFilter: null,
      filterSaveName: "",
      filterValues: [],
      isFetchingValues: false,
      isOverridingFilter: false,
      selectedFilters: [],
      isPublic: false,
      errorMessage: null,
    },
    props.appName,
    props.appID,
    "HawkeyeV2CreateFilterPanel",
    props.componentID
  );

  // Get filter values on selected filter change
  useEffect(() => {
    if (state.selectedFilter) {
      dispatch({
        isFetchingValues: true,
        filterValues: [],
      });
      dispatch(
        {
          filter: state.selectedFilter,
          smartFilterRefs: smartFilterRefs,
          HawkeyeV2AppSettings: HawkeyeV2AppSettings,
        },
        "AxiomEvent",
        "getFilterValues"
      );
    }
  }, [dispatch, state.selectedFilter, smartFilterRefs, HawkeyeV2AppSettings]);

  const onFilterSelection = useCallback(
    (selectedFilter) => {
      const d1 = { ...selectedFilter };
      delete d1["component"];
      dispatch({ selectedFilter: d1 });
    },
    [dispatch]
  );

  const onFilterValueSelection = useCallback(
    (selectedValues) => {
      const filterValues = selectedValues.map((value) => value.displayName);
      const newSelectedFilters = getSelectedFilters(
        state.selectedFilters,
        state.selectedFilter,
        filterValues
      );

      dispatch(
        {
          filters: [
            {
              filter: state.selectedFilter,
              values: filterValues,
            },
          ],
          smartFilterRefs: smartFilterRefs,
        },
        "AxiomEvent",
        "onApplyFilter"
      );

      dispatch({
        selectedFilters: newSelectedFilters.filter((f) => f.values.length),
        errorMessage: null,
      });
    },
    [dispatch, state.selectedFilter, state.selectedFilters, smartFilterRefs]
  );

  const resetSavedFilters = useCallback(() => {
    if (state.selectedFilters.length > 0) {
      dispatch(
        (state) => ({
          filters: state.selectedFilters,
          smartFilterRefs: smartFilterRefs,
        }),
        "AxiomEvent",
        "onRemoveFilter"
      );
    }
  }, [dispatch, smartFilterRefs, state.selectedFilters.length]);

  const closeCreateWindow = useCallback(() => {
    resetSavedFilters();
    closeCreateFilter();
  }, [closeCreateFilter, resetSavedFilters]);

  const onSaveFilter = (isSaveFilterRequired = true) => {
    if (!state.filterSaveName && isSaveFilterRequired == true) {
      dispatch({
        errorMessage: "Please provide the filter name",
      });
      return;
    } else if (state.selectedFilters.length === 0 && isSaveFilterRequired == true) {
      dispatch({
        errorMessage: "Please select filter to save",
      });
      return;
    } else if (state.selectedFilters.length === 0 && isSaveFilterRequired == false) {
      dispatch({
        errorMessage: "Please select filter to apply",
      });
      return;
    } else {
      dispatch({
        errorMessage: null,
      });
    }
    const { filterSaveName, selectedFilters } = state;
    // Check if a filter with similar name exists in saved filters
    const isAlreadySaved = savedFilters.some((f) => f.filterName === filterSaveName);

    // If user is not overriding any saved filter and current filter is not saved
    // save it in the database
    if (!state.isOverridingFilter && !isAlreadySaved) {
      saveFilter(
        {
          widget: widget,
          name: filterSaveName,
          filters: selectedFilters.map((f) => ({
            filter: {
              displayName: f.filter.displayName,
              key: f.filter.key,
            },
            values: f.values,
          })),
          isPublic: state.isPublic,
        },
        isSaveFilterRequired
      );
      closeCreateWindow();
    }

    // If the filter is already saved and user agreed to override current filter then
    // update it in the database
    if (isAlreadySaved && state.isOverridingFilter) {
      updateSavedFilter(
        {
          widget: widget,
          filterID: savedFilters.find((f) => f.filterName === filterSaveName).filterID,
          filterName: filterSaveName,
          filters: selectedFilters.map((f) => ({
            filter: {
              displayName: f.filter.displayName,
              key: f.filter.key,
            },
            values: f.values,
          })),
        },
        isSaveFilterRequired
      );
      closeCreateWindow();
    }

    // If the current filter is already saved prompt user if he wants to override it
    if (isAlreadySaved && !state.isOverridingFilter) {
      dispatch({ isOverridingFilter: true });
    }
  };

  const updateFilterSaveName = (event) => {
    const filterSaveName = event.target.value;
    dispatch({
      filterSaveName,
      isOverridingFilter: false,
      errorMessage: null,
    });
  };

  const onResetAll = () => {
    dispatch({
      isReset: true,
      selectedFilter: null,
      filterSaveName: "",
      filterValues: [],
      isFetchingValues: false,
      isOverridingFilter: false,
      selectedFilters: [],
      isPublic: false,
      errorMessage: null,
    });
    resetSavedFilters();
  };
  const filterOptionsData = useMemo(() => {
    if (state.isReset == true) {
      dispatch({
        isReset: false,
      });
    }
    return filterOptions.map((d) => {
      return { ...d };
    });
  }, [filterOptions, dispatch, state.isReset]);
  return (
    <div className="HawkeyeV2CreateFilterPanel">
      <div className="top-bar">
        Filter Data
        <div className="close-button" onClick={closeCreateWindow}>
          <Icon icon="ICON_CLOSE" className="hawkeye-close-tag-icon" />
        </div>
      </div>
      <FilterPanelOptions
        appID={props.appID}
        appName={props.appName}
        componentID={`${props.componentID}_FilterOptions`}
        filterOptions={filterOptionsData}
        onSelection={onFilterSelection}
        isFetchingValues={state.isFetchingValues}
        filterValues={state.filterValues}
        onFilterValueSelection={onFilterValueSelection}
        selectedFilters={state.selectedFilters}
        selectedFilter={state.selectedFilter}
      ></FilterPanelOptions>
      <div className="save-filters">
        <div className="save-filter-label">Save Filter</div>
        <div className="filter-value-button">
          <div className="filter-value">
            <input
              type="text"
              placeholder="Enter filter name to save"
              value={state.filterSaveName}
              onChange={updateFilterSaveName}
              className={`${state.errorMessage != null ? "error-message" : ""}`}
            ></input>
          </div>
          <div
            className="save-button"
            onClick={() => {
              onSaveFilter(true);
            }}
          >
            {" "}
            SAVE{" "}
          </div>
        </div>
        {state.errorMessage != null && (
          <div className="error-message-label">
            <Icon icon="ICON_ERROR" className="hawkeye-filter-error-icon" />
            {state.errorMessage}
          </div>
        )}
        <div className="reset-filter-button">
          <div className="reset-button" onClick={onResetAll}>
            RESET
          </div>
          <div
            className="filter-button"
            onClick={() => {
              onSaveFilter(false);
            }}
          >
            FILTER RESULT
          </div>
        </div>
      </div>
    </div>
  );
}

export default memo(HawkeyeV2CreateFilterPanel);
